// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'

import { ScannerContainer as Container } from 'mytours-read-common'
import { IonSlides, IonSlide } from '@ionic/react'

import Page from '../../common/components/Page'

import './style.less'
import strings from 'mobile_web/strings'
import SegmentButtons from 'mobile_web/modules/common/components/SegmentButtons'
import Keypad from './parts/Keypad'
import ScanEntry from 'mobile_web/modules/common/components/scan_entry/ScanEntry'
import { SegmentButton, SegmentButtonValue } from '../types'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import { Search } from './parts/Search'

interface OwnProps {
	isPageContent?: boolean
	successCallback?: () => void
	errorCallback?: (error: any) => void
}

type Props = Container.Props & Container.Actions & RouteComponentProps & OwnProps

const Scanner: React.FC<Props> = ({
	qrEnabled,
	keypadEnabled,
	searchEnabled,
	items,
	cleanItems,
	loadItemOnKeypadEntry,
	keypadEntryStatus,
	loading,
	isPageContent,
	successCallback,
	errorCallback,
	searchIndexes
}) => {
	const [swiper, setSwiper] = useState<any>()
	const buttonsTemplate: SegmentButton[] = [
		{
			value: SegmentButtonValue.Search,
			label: strings.live_tours_search,
		},
		{
			value: SegmentButtonValue.EnterCode,
			label: strings.enter_code,
		},
		{
			value: SegmentButtonValue.Scan,
			label: strings.scan,
		},
	]

	const [pageState, setPageState] = useState<SegmentButtonValue>()
	const [buttons, setButtons] = useState<SegmentButton[]>([])

	useEffect(() => {
		const filteredButtons = buttonsTemplate.filter(button => {
			if (
				(!searchEnabled && button.value === SegmentButtonValue.Search) ||
				(!keypadEnabled && button.value === SegmentButtonValue.EnterCode) ||
				(!qrEnabled && button.value === SegmentButtonValue.Scan)
			) {
				return false
			}

			return true
		})

		setButtons(filteredButtons)
	}, [])

	useEffect(() => {
		setPageState(buttons?.[0]?.value)
	}, [buttons])

	const initSlider = async function (this: any): Promise<void> {
		setSwiper(await this.getSwiper())
	}

	useEffect(() => {
		if (swiper && pageState) {
			buttons.forEach((button, index) => {
				if (pageState === button.value) {
					swiper.slideTo(index)
				}
			})
		}
	}, [pageState])

	const handleChangeSlide = (): void => {
		const activeIndex = swiper?.activeIndex
		if (activeIndex !== undefined) {
			setPageState(buttons[activeIndex].value)
		}
	}

	const renderContent = () => {
		if (buttons.length > 1) {
			return (
				<>
					<IonSlides
						className="scanner-content-slider"
						onIonSlidesDidLoad={initSlider}
						onIonSlideDidChange={handleChangeSlide}
					>
						{/* For some reason, the slide with search is always shown on mount regardless of the order of `IonSlide` */}
						{/* We must put it in the first `IonSlide` or else `IonSlides` navigation (slide or SegmentButtons) will be buggy on mount */}
						{searchEnabled && (
							<IonSlide>
								<Search searchIndexes={searchIndexes} />
							</IonSlide>
						)}
						{keypadEnabled && (
							<IonSlide>
								<Keypad
									keypadEntryStatus={keypadEntryStatus}
									loadItemOnKeypadEntry={loadItemOnKeypadEntry}
									items={items}
									cleanItems={cleanItems}
									loading={loading}
								/>
							</IonSlide>
						)}
						{qrEnabled && (
							<IonSlide>
								{/* Only show when tab is selected so that we don't keep using camera permission */}
								{pageState === SegmentButtonValue.Scan && <ScanEntry successCallback={successCallback} errorCallback={errorCallback} />}
							</IonSlide>
						)}
					</IonSlides>
					<SegmentButtons
						selectedValue={pageState}
						onChange={value => setPageState(value as SegmentButtonValue)}
						buttons={buttons}
					/>
				</>
			)
		}

		switch (pageState) {
			case SegmentButtonValue.Scan:
				return <ScanEntry successCallback={successCallback} errorCallback={errorCallback} />
			case SegmentButtonValue.EnterCode:
				return (
					<Keypad
						keypadEntryStatus={keypadEntryStatus}
						loadItemOnKeypadEntry={loadItemOnKeypadEntry}
						items={items}
						cleanItems={cleanItems}
						loading={loading}
					/>
				)
			case SegmentButtonValue.Search:
				return <Search searchIndexes={searchIndexes} />
			default:
				return <div />
		}
	}

	if (isPageContent) {
		return renderContent()
	}

	return (
		<Page
			contentProps={{ className: 'scanner-content' }}
			footer={{
				component: <AudioPlayerBarComponent bottomOffset={-1} />,
			}}
			dataCy="page-scanner"
		>
			{renderContent()}
		</Page>
	)
}

const ScannerComponent = Container.default(Scanner)
export default ScannerComponent as any
